import React from "react";
import { createFileRoute, Navigate } from "@tanstack/react-router";

import { useIssueView } from "#app/components/issue-view";
import { LoadErrorState } from "#app/components/settings/load-error-state";
import { resolveLandingView, useLastView } from "#app/lib/views/last-view";
import { useViews } from "#app/lib/views/view-context";
import { viewPath } from "#app/lib/views/view-routes";
import type { Omittable } from "@salestrak/ui";
import { routePath } from "#app/lib/hooks/use-app-navigate";

/**
 * `/workflow/$workflowId` — no view of its own; it lands on the view the
 * viewer last had open here, else the workflow's default view.
 *
 * The memory is the viewer's (`useLastView`); the default is a property of
 * the saved view row (the "Set as default view" switch in the tab menu and
 * the Customize view panel) and is where a first visit lands. Both resolve
 * async, so this waits for the tab row and the memory rather than hardcoding
 * a segment. The switch turns off too: a workflow with no default opens on
 * its first view in the row. `/board` remains the floor for the case the
 * server seeds nothing.
 */
function WorkflowIndex() {
  const { workspace, workflowId } = Route.useParams();
  const { issue: issueParam } = Route.useSearch();
  const { openIssueById } = useIssueView();
  const { scope, views, initialLoading, loadError, retry } = useViews();
  const { lastKey, initialLoading: lastViewLoading } = useLastView(scope);

  // Deep link: ?issue=<id> should open the issue view. Handle it here — before
  // the redirect to the default view — so a downstream effect isn't relied
  // upon. We open the issue directly, then redirect to a clean URL.
  const issueFromQuery =
    issueParam ??
    (typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("issue")
      : null);

  const deepLinkHandled = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!issueFromQuery) return;
    if (deepLinkHandled.current === issueFromQuery) return;
    deepLinkHandled.current = issueFromQuery;
    openIssueById(issueFromQuery, { source: "deep_link" });
  }, [issueFromQuery, openIssueById]);

  const target = React.useMemo(() => {
    const view = resolveLandingView(views, lastKey);
    return view
      ? viewPath(scope, view)
      : "/workflow/" + workflowId + "/board";
  }, [views, lastKey, scope, workflowId]);

  // All three resolve async, and deciding early sends everyone to whichever
  // view sorts first — landing on the right one is the whole point of this
  // route.
  // An empty pane, not a skeleton: the view's own skeleton follows the
  // redirect, and a stand-in here would be a second, different one.
  if (initialLoading || lastViewLoading) {
    return null;
  }

  // A failed load has no default to land on — and the `/board` floor would
  // only render an empty list of its own. Show the failure where the person
  // is; a retry re-asks for the row and the redirect proceeds when it lands.
  if (views.length === 0 && loadError) {
    return <LoadErrorState error={loadError} noun="views" onRetry={retry} />;
  }

  return <Navigate to={routePath(`/${workspace}${target}`)} replace />;
}

export const Route = createFileRoute("/(app)/$workspace/workflow/$workflowId/")({
  validateSearch: (
    search: Record<string, unknown>,
    // A search param is written back as an explicit `undefined` to clear it —
    // the router reads that as "absent from the URL" — so the member relaxes.
  ): Omittable<{ issue?: string }> => ({
    issue: typeof search.issue === "string" ? search.issue : undefined,
  }),
  component: WorkflowIndex,
});
